import { useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback, useMemo } from 'react'
import useSWR from 'swr'

import type { ChatTurn } from './chatStream'

/**
 * The site's agent conversations. They live in Rolodex and are shared by
 * everyone at the plant: a build is a plant record, and the next shift should
 * be able to open it rather than be told a transcript expired with a browser
 * tab.
 */

export interface ConversationSummary {
  id: string
  title: string
  created_at?: string
  updated_at?: string
  turn_count: number
  /** Who spoke last, since the list is shared. */
  last_author?: string
}

export interface Conversation extends ConversationSummary {
  turns: ChatTurn[]
}

export const conversationsBase = (
  fimServiceUrl: string,
  siteId: string
): string =>
  `${fimServiceUrl}/api/v1/sites/${encodeURIComponent(siteId)}/sentinel-chat/conversations`

export const useConversations = (siteId: string | null) => {
  const { t } = useLingui()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const base = siteId ? conversationsBase(FIM_SERVICE_URL, siteId) : null

  const { data, error, isLoading, mutate } = useSWR(
    base,
    async (url: string) => {
      const { data } = await authorizedAxios.get<{
        conversations: ConversationSummary[]
      }>(url)
      return data.conversations ?? []
    }
  )

  const create = useCallback(
    async (title = ''): Promise<Conversation | null> => {
      if (!base) return null
      const { data } = await authorizedAxios.post<{
        conversation: Conversation
      }>(base, { title })
      await mutate()
      return { ...data.conversation, turns: data.conversation.turns ?? [] }
    },
    [authorizedAxios, base, mutate]
  )

  const load = useCallback(
    async (id: string): Promise<Conversation | null> => {
      if (!base) return null
      const { data } = await authorizedAxios.get<{
        conversation: Conversation
      }>(`${base}/${encodeURIComponent(id)}`)
      return { ...data.conversation, turns: data.conversation.turns ?? [] }
    },
    [authorizedAxios, base]
  )

  const rename = useCallback(
    async (id: string, title: string): Promise<void> => {
      if (!base) return
      await authorizedAxios.patch(`${base}/${encodeURIComponent(id)}`, {
        title,
      })
      await mutate()
    },
    [authorizedAxios, base, mutate]
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      if (!base) return
      await authorizedAxios.delete(`${base}/${encodeURIComponent(id)}`)
      await mutate()
    },
    [authorizedAxios, base, mutate]
  )

  return useMemo(
    () => ({
      conversations: data ?? [],
      loading: isLoading,
      error: error ? t`Could not load past conversations.` : null,
      refresh: mutate,
      create,
      load,
      rename,
      remove,
    }),
    [create, data, error, isLoading, load, mutate, remove, rename, t]
  )
}
